import { supabase } from '../supabase/client';

export interface PackagePrice {
  id: string;
  orgId: string;
  catId: string;
  levelId: string;
  monthPrice: any;
  quarterPrice: any;
  halfYearPrice: any;
  yearPrice: any;
  isEnabled: boolean;
}

class PriceStore {
  /**
   * 根据名称（组织名、分类名或code、级别名）查询定价
   * 支持分类字段同时匹配 code（如 "scratch"）和 name（如 "图形化编程"）
   */
  async findPriceByNames(orgName: string, catName: string | null, levelName: string | null): Promise<PackagePrice | undefined> {
    // 1. 查询组织
    const { data: orgData } = await supabase
      .from('organizations')
      .select('id')
      .eq('name', orgName)
      .eq('is_enabled', true)
      .maybeSingle();
    if (!orgData) {
      return undefined;
    }

    // 2. 没有分类时，按组织取任意一条启用价格
    if (!catName) {
      const { data: levelData } = await supabase
        .from('exam_levels')
        .select('*')
        .eq('is_enabled', true)
        .limit(1)
        .maybeSingle();
      if (!levelData) return undefined;
      return this.buildPackagePrice(levelData, orgData.id.toString(), levelData.cat_id?.toString() || '');
    }

    // 3. 查询分类：同时匹配 code 和 name，避免 papers.category_type 字段值不一致
    const { data: catData } = await supabase
      .from('course_categories')
      .select('id')
      .eq('org_id', orgData.id)
      .eq('is_enabled', true)
      .or(`code.eq.${catName},name.eq.${catName}`)
      .maybeSingle();
    if (!catData) {
      return undefined;
    }

    // 4. 没有级别时，按分类取任意一条启用价格
    if (!levelName) {
      const { data: levelData } = await supabase
        .from('exam_levels')
        .select('*')
        .eq('cat_id', catData.id)
        .eq('is_enabled', true)
        .limit(1)
        .maybeSingle();
      if (!levelData) return undefined;
      return this.buildPackagePrice(levelData, orgData.id.toString(), catData.id.toString());
    }

    // 5. 按级别名精确匹配
    const { data: levelData } = await supabase
      .from('exam_levels')
      .select('*')
      .eq('level_name', levelName)
      .eq('cat_id', catData.id)
      .eq('is_enabled', true)
      .maybeSingle();
    if (!levelData) {
      return undefined;
    }

    return this.buildPackagePrice(levelData, orgData.id.toString(), catData.id.toString());
  }

  /**
   * 构造 PackagePrice 返回对象（统一数字转换）
   */
  private buildPackagePrice(levelData: any, orgId: string, catId: string): PackagePrice {
    return {
      id: levelData.id.toString(),
      orgId,
      catId,
      levelId: levelData.id.toString(),
      monthPrice: Number(levelData.price_month) || 0,
      quarterPrice: Number(levelData.price_quarter) || 0,
      halfYearPrice: Number(levelData.price_half_year) || 0,
      yearPrice: Number(levelData.price_year) || 0,
      isEnabled: true,
    };
  }

  async findPriceByIds(orgId: string, catId: string, levelId: string): Promise<PackagePrice | undefined> {
    const { data: levelData } = await supabase
      .from('exam_levels')
      .select('*')
      .eq('id', parseInt(levelId))
      .eq('is_enabled', true)
      .maybeSingle();
    if (!levelData) return undefined;

    return this.buildPackagePrice(levelData, orgId, catId);
  }

  async getAllPrices(): Promise<PackagePrice[]> {
    const { data: levelsData } = await supabase
      .from('exam_levels')
      .select('*')
      .eq('is_enabled', true);

    if (!levelsData) return [];

    return levelsData.map((level: any) => this.buildPackagePrice(level, level.cat_id?.toString() || '', level.cat_id?.toString() || ''));
  }
}

export const priceStore = new PriceStore();
